import { z } from "zod";
import { Id, IsoDateTime, Minor, OptionalText, Page, PageSize } from "./common";
import { defineRoutes } from "./define";
import { PatientSnapshot } from "./patients";
import { PROBLEMS } from "./problem";

/* Mirrors core/modules/invoices (dates as ISO strings, money in UAH kopecks). */

export const INVOICE_STATUSES = ["issued", "paid", "void"] as const;
export const InvoiceStatus = z.enum(INVOICE_STATUSES);
export const InvoiceStatusFilter = z.enum(["unpaid", "all", ...INVOICE_STATUSES]);
export const InvoiceLineKind = z.enum(["visit", "package", "gift_card", "custom"]);
/** Derived payment state of a visit/booking (§3.4) — never stored. */
export const VisitPaymentStatus = z.enum(["unbilled", "invoiced", "paid", "package"]);

export const InvoiceListItem = z.object({
  id: Id,
  number: z.string(),
  patientId: Id.nullable(),
  patientName: z.string().nullable(),
  status: InvoiceStatus,
  totalMinor: Minor,
  issuedAt: IsoDateTime,
});
export type InvoiceListItem = z.infer<typeof InvoiceListItem>;

export const InvoiceListData = z.object({
  items: z.array(InvoiceListItem),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
  counts: z.object({
    unpaid: z.number().int(),
    all: z.number().int(),
    issued: z.number().int(),
    paid: z.number().int(),
    void: z.number().int(),
  }),
});

export const WorklistRow = z.object({
  visitId: Id,
  occurredAt: IsoDateTime,
  specialistName: z.string().nullable(),
  agingDays: z.number().int(),
  amountMinor: Minor.nullable(),
  promoPct: z.number().int(),
});
export const WorklistGroup = z.object({
  patientId: Id,
  patientName: z.string(),
  sumMinor: Minor,
  rows: z.array(WorklistRow),
});
/** US-10: the clinic-wide billing queue, quoted. */
export const BillingWorklist = z.object({
  groups: z.array(WorklistGroup),
  totalCount: z.number().int(),
  totalMinor: Minor,
});
export type BillingWorklist = z.infer<typeof BillingWorklist>;

export const UncapturedBooking = z.object({
  appointmentId: Id,
  startsAt: IsoDateTime,
  patientId: Id,
  patientName: z.string(),
  specialistName: z.string().nullable(),
  payment: VisitPaymentStatus,
});
export const ScheduledBooking = UncapturedBooking.extend({ expectedMinor: Minor.nullable() });

/** The /invoices screen in one read. */
export const InvoiceListView = z.object({
  invoices: InvoiceListData,
  worklist: BillingWorklist,
  uncaptured: z.array(UncapturedBooking),
  scheduled: z.array(ScheduledBooking),
});
export type InvoiceListView = z.infer<typeof InvoiceListView>;

export const InvoiceDetailLine = z.object({
  id: Id,
  kind: InvoiceLineKind,
  title: z.string(),
  qty: z.number().int(),
  unitPriceMinor: Minor,
  discountPct: z.number().int(),
  totalMinor: Minor,
  active: z.boolean(),
  visitId: Id.nullable(),
  giftCardId: Id.nullable(),
});
export const InvoiceGiftRedemption = z.object({
  id: Id,
  code: z.string(),
  giftCardId: Id,
  amountMinor: Minor,
  at: IsoDateTime,
  reversedAt: IsoDateTime.nullable(),
});
export const InvoiceDetail = z.object({
  id: Id,
  number: z.string(),
  patientId: Id.nullable(),
  patientName: z.string().nullable(),
  status: InvoiceStatus,
  totalMinor: Minor,
  issuedAt: IsoDateTime,
  paidAt: IsoDateTime.nullable(),
  voidAt: IsoDateTime.nullable(),
  note: z.string().nullable(),
  lines: z.array(InvoiceDetailLine),
  giftRedemptions: z.array(InvoiceGiftRedemption),
});
/** The printable invoice + what the viewer may still do with certificates. */
export const InvoiceDetailView = z.object({
  invoice: InvoiceDetail,
  redeemedMinor: Minor,
  giftSaleCardId: Id.nullable(),
  giftSuggestions: z.array(z.object({ code: z.string(), remainingMinor: Minor })),
});
export type InvoiceDetailView = z.infer<typeof InvoiceDetailView>;

export const DraftVisitOption = z.object({
  id: Id,
  occurredAt: IsoDateTime,
  episodeTitle: z.string().nullable(),
  amountMinor: Minor.nullable(),
  promoPct: z.number().int().nullable(),
});
export const DraftAppointmentOption = z.object({
  id: Id,
  startsAt: IsoDateTime,
  amountMinor: Minor.nullable(),
  promoPct: z.number().int().nullable(),
});
/** The "new invoice" screen for one patient. */
export const InvoiceDraft = z.object({
  patient: PatientSnapshot,
  visits: z.array(DraftVisitOption),
  priceMissing: z.boolean(),
  appointments: z.array(DraftAppointmentOption),
  giftBalanceMinor: Minor,
});
export type InvoiceDraft = z.infer<typeof InvoiceDraft>;

export const GiftApplication = z.object({ giftCardId: Id, code: z.string(), amountMinor: Minor });

/** Visit/booking lines are priced server-side; only custom lines carry a client amount. */
export const IssueInvoiceBody = z.object({
  patientId: Id,
  note: OptionalText(500),
  visitIds: z.array(Id).max(100).default([]),
  appointmentIds: z.array(Id).max(100).default([]),
  custom: z
    .array(z.object({ title: z.string().trim().min(1).max(200), amountMinor: Minor }))
    .max(50)
    .default([]),
});
export type IssueInvoiceBody = z.infer<typeof IssueInvoiceBody>;

export const IssuedInvoice = z.object({
  id: Id,
  number: z.string(),
  status: InvoiceStatus,
  totalMinor: Minor,
  patientId: Id.nullable(),
  /** Certificates auto-applied in the issuing transaction (may have flipped it to paid). */
  giftApplications: z.array(GiftApplication),
});
export type IssuedInvoice = z.infer<typeof IssuedInvoice>;

export const TransitionInvoiceBody = z.object({ transition: z.enum(["paid", "void"]) });
export const TransitionedInvoice = z.object({
  id: Id,
  number: z.string(),
  status: InvoiceStatus,
  /** Void only: certificate redemptions returned to their cards. */
  reversedGifts: z.array(GiftApplication),
});

export const invoices = defineRoutes({
  list: {
    method: "GET",
    path: "/invoices",
    summary: "The /invoices screen: filtered list + billing worklist + booking pipeline",
    query: z.object({
      status: InvoiceStatusFilter.default("unpaid"),
      q: z.string().max(120).optional(),
      page: Page,
      pageSize: PageSize,
    }),
    responses: { 200: InvoiceListView, 401: PROBLEMS[401], 403: PROBLEMS[403] },
  },
  worklist: {
    method: "GET",
    path: "/invoices/worklist",
    summary: "US-10 billing queue: every unbilled visit, quoted, oldest first",
    query: z.object({ limit: z.coerce.number().int().min(1).max(500).default(100) }),
    responses: { 200: BillingWorklist, 401: PROBLEMS[401], 403: PROBLEMS[403] },
  },
  draft: {
    method: "GET",
    path: "/patients/:patientId/invoice-draft",
    summary: "Billable visits, prepayable bookings and certificate balance for a new invoice",
    params: z.object({ patientId: Id }),
    responses: { 200: InvoiceDraft, 401: PROBLEMS[401], 403: PROBLEMS[403], 404: PROBLEMS[404] },
  },
  detail: {
    method: "GET",
    path: "/invoices/:invoiceId",
    summary: "Printable invoice; certificate suggestions when the viewer may apply them",
    params: z.object({ invoiceId: Id }),
    responses: {
      200: InvoiceDetailView,
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      404: PROBLEMS[404],
    },
  },
  issue: {
    method: "POST",
    path: "/invoices",
    summary:
      "Issue an immutable invoice (visits/bookings priced server-side, custom lines, auto-applied certificates)",
    body: IssueInvoiceBody,
    responses: {
      201: IssuedInvoice,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      409: PROBLEMS[409],
      422: PROBLEMS[422],
    },
  },
  transition: {
    method: "POST",
    path: "/invoices/:invoiceId/transition",
    summary: "Mark paid, or void (releases visits and reverses certificate redemptions)",
    params: z.object({ invoiceId: Id }),
    body: TransitionInvoiceBody,
    responses: {
      200: TransitionedInvoice,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      404: PROBLEMS[404],
      422: PROBLEMS[422],
    },
  },
});
